import { useState, useEffect } from "react";
import { Modal, Button, Row, Col, Card, Spinner, Badge } from "react-bootstrap";
import { format } from "date-fns";
import { formatCurrency } from "../../../../utils/numberFormatter";
import axios from "axios";
import swal from "sweetalert";
import BASE_URL from "../../../../assets/global/url";

const CashFlowView = ({
  show,
  onHide,
  transactionId,
  userLoggedID,
  onSuccess,
  getBadgeConfig,
}) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isConfirming, setIsConfirming] = useState(false);

  useEffect(() => {
    const fetchDetails = async () => {
      if (!transactionId || !show) return;

      setLoading(true);
      try {
        const response = await axios.get(
          `${BASE_URL}/cashFlow/v2/getCashFlow/${transactionId}`,
        );
        setData(response.data);
      } catch (error) {
        console.error("Failed to fetch transaction details:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchDetails();
  }, [transactionId, show]);

  const formatDate = (dateString) => {
    if (!dateString) return "---";
    return format(new Date(dateString), "MMM dd, yyyy");
  };

  const projectedDeduction =
    data?.projected_effect?.senderDeduction ?? data?.amount;
  const projectedAddition =
    data?.projected_effect?.receiverAddition ?? data?.amount;
  const isInsufficient = data?.projected_effect?.isInsufficient ?? false;
  // hasCutoff is injected by /v2/getCashFlow/:id — false when the cutoff
  // covering transaction_date is deleted or doesn't exist
  const hasNoCutoff = data ? !data.hasCutoff : false;

  const handleConfirm = async () => {
    if (!userLoggedID) {
      swal("Error", "User authentication missing.", "error");
      return;
    }

    const willConfirm = await swal({
      title: "Confirm Transaction",
      text: "Are you sure you want to confirm this transaction? This action will transfer the funds and update the account balances.",
      icon: "warning",
      buttons: ["Cancel", "Yes, Confirm"],
      dangerMode: false,
    });

    if (!willConfirm) {
      return;
    }

    setIsConfirming(true);
    try {
      await axios.put(
        `${BASE_URL}/cashFlow/v2/confirmTransferMoney/${transactionId}/${userLoggedID}`,
        { selectedDate: data.transaction_date },
      );

      swal(
        "Success",
        "Cash flow confirmed and funds transferred successfully.",
        "success",
      );

      if (onSuccess) onSuccess();
      onHide();
    } catch (error) {
      if (error.response.status === 401) {
        swal({
          icon: "error",
          // title: "Please contact your support",
          title: error.response.data.title,
          text: error.response.data.message,
        }); // test 1
      } else {
        console.error("Confirmation Error:", error);
        const errorMessage =
          error.response?.data?.message || "Failed to confirm transaction.";
        swal("Error", errorMessage, "error");
      }
    } finally {
      setIsConfirming(false);
    }
  };

  const handleReject = () => {
    console.log("TODO: Reject Cash Flow transaction ID:", transactionId);
    swal(
      "Info",
      "Reject functionality is currently under construction.",
      "info",
    );
  };

  const renderAccountDetails = (
    account,
    title,
    type,
    affectAmount,
    receiverTxn = null,
    status,
  ) => {
    if (!account)
      return (
        <Card className="h-100 shadow-sm border-0 bg-light">
          <Card.Body className="d-flex align-items-center justify-content-center text-muted">
            No {title} recorded
          </Card.Body>
        </Card>
      );

    const isPending = status === "Pending";
    // A pending destination account's stored balance already includes
    // this transfer's amount, so back it out here to show the true
    // starting balance — Net Balance below is then just that raw value.
    const isDestinationPendingCredit = type === "destination" && isPending;

    const displayBalance =
      type === "destination" && receiverTxn && !isPending
        ? receiverTxn.account_balance
        : isDestinationPendingCredit
          ? account.amount - affectAmount
          : account.amount;

    const netBalance =
      type === "source"
        ? displayBalance - affectAmount
        : isDestinationPendingCredit
          ? account.amount
          : displayBalance + affectAmount;
    const isSourceInsufficient = type === "source" && netBalance < 0;

    return (
      <Card
        className={`h-100 shadow-sm border ${isPending && isSourceInsufficient ? "border-danger" : "border-0"}`}
      >
        <Card.Header
          className={`${isPending && isSourceInsufficient ? "bg-danger" : "bg-primary"} text-white fw-bold`}
        >
          <i className="bx bxs-bank me-2"></i>
          {title}
        </Card.Header>
        <Card.Body>
          <h5 className="text-dark fw-bold mb-1">{account.account_name}</h5>
          <p className="text-muted small mb-3">
            {account.account_list_base_sub?.module_type ||
              "Unknown Module Type"}
          </p>

          <div className="d-flex justify-content-between border-bottom pb-2 mb-2">
            <span className="text-secondary">Current Balance:</span>
            <span className="fw-bold">
              {formatCurrency(displayBalance, account.currency?.currency_name)}
            </span>
          </div>

          <div className="d-flex justify-content-between pb-2 mb-2">
            <span className="text-secondary">Currency:</span>
            <span className="fw-bold">
              {account.currency?.currency_name || "---"}
            </span>
          </div>

          {isPending && affectAmount !== undefined && affectAmount !== null ? (
            <div
              className={`mt-3 pt-3 border-top ${isSourceInsufficient ? "bg-danger bg-opacity-10" : "bg-light"} p-3 rounded`}
            >
              <h6
                className="fw-bold mb-3 text-muted text-uppercase"
                style={{ fontSize: "0.8rem" }}
              >
                Projected Transaction Effect
              </h6>

              <div className="d-flex justify-content-between mb-2">
                <span className="text-secondary">
                  Amount {type === "source" ? "Deducted" : "Received"}:
                </span>
                <span
                  className={`fw-bold ${type === "source" ? "text-danger" : "text-success"}`}
                >
                  {type === "source" ? "-" : "+"}{" "}
                  {formatCurrency(
                    Math.abs(affectAmount),
                    account.currency?.currency_name,
                  )}
                </span>
              </div>

              <div className="d-flex justify-content-between">
                <span className="text-secondary fw-semibold">Net Balance:</span>
                <span
                  className={`fw-bold ${isSourceInsufficient ? "text-danger fs-5" : "text-dark"}`}
                >
                  {formatCurrency(netBalance, account.currency?.currency_name)}
                </span>
              </div>
            </div>
          ) : (
            affectAmount !== undefined &&
            affectAmount !== null && (
              <div className="d-flex justify-content-between mt-3 pt-2 bg-light p-2 rounded">
                <span className="text-secondary fw-semibold">
                  Recorded Effect:
                </span>
                <span
                  className={`fw-bold ${type === "source" ? "text-danger" : "text-success"}`}
                >
                  {type === "source" ? "-" : "+"}{" "}
                  {formatCurrency(
                    Math.abs(affectAmount),
                    account.currency?.currency_name,
                  )}
                </span>
              </div>
            )
          )}
        </Card.Body>
      </Card>
    );
  };

  return (
    <Modal
      show={show}
      onHide={onHide}
      size="lg"
      centered
      backdrop="static"
      scrollable
    >
      <Modal.Header closeButton className="bg-light">
        <Modal.Title className="fw-bold fs-5">
          <i className="bx bx-info-circle me-2 text-primary"></i>
          Cash Flow Information
        </Modal.Title>
      </Modal.Header>

      <Modal.Body className="p-4">
        {loading ? (
          <div
            className="d-flex justify-content-center align-items-center"
            style={{ minHeight: "200px" }}
          >
            <Spinner animation="border" variant="primary" />
          </div>
        ) : !data ? (
          <div className="text-center text-muted py-5">
            Data could not be loaded.
          </div>
        ) : (
          <>
            <Row className="mb-4 text-center g-3">
              <Col md={4}>
                <div className="p-3 border rounded bg-light h-100 d-flex flex-column justify-content-center">
                  <small className="text-muted d-block text-uppercase mb-1">
                    Amount
                  </small>
                  <span className="fs-4 fw-bold text-dark">
                    {formatCurrency(
                      data.amount,
                      data.account_list_id_cash_tos?.currency?.currency_name ||
                        data.account_list_id_cash_froms?.currency
                          ?.currency_name,
                    )}
                  </span>
                </div>
              </Col>

              <Col md={4}>
                <div className="p-3 border rounded bg-light h-100 d-flex flex-column justify-content-center align-items-center">
                  <small className="text-muted d-block text-uppercase mb-2">
                    Status
                  </small>
                  <Badge {...getBadgeConfig(data.status)}>
                    {data.status || "---"}
                  </Badge>
                </div>
              </Col>

              <Col md={4}>
                <div className="p-3 border rounded bg-light h-100 d-flex flex-column justify-content-center">
                  <small className="text-muted d-block text-uppercase mb-1">
                    Process Date
                  </small>
                  <span className="fs-5 fw-bold text-dark">
                    {formatDate(data.transaction_date)}
                  </span>
                </div>
              </Col>
            </Row>

            <Row className="mb-4">
              <Col sm={6}>
                <p className="mb-1">
                  <span className="text-muted">Transaction No:</span>{" "}
                  <strong className="ms-1">
                    {data.transaction_number || "---"}
                  </strong>
                </p>
              </Col>
              <Col sm={6}>
                <p className="mb-1">
                  <span className="text-muted">Module From:</span>{" "}
                  <strong className="ms-1">{data.module_from || "---"}</strong>
                </p>
              </Col>
            </Row>

            <Row className="g-4 mb-4">
              <Col md={6}>
                {renderAccountDetails(
                  data.account_list_id_cash_froms,
                  "Source Account",
                  "source",
                  projectedDeduction,
                  null,
                  data.status,
                )}
              </Col>
              <Col md={6}>
                {renderAccountDetails(
                  data.account_list_id_cash_tos,
                  "Destination Account",
                  "destination",
                  data.receiver_transaction?.amount || projectedAddition,
                  data.receiver_transaction,
                  data.status,
                )}
              </Col>
            </Row>

            <div className="bg-light p-3 rounded border">
              <h6 className="fw-bold text-muted mb-2">Description</h6>
              <p className="mb-0 text-dark" style={{ whiteSpace: "pre-wrap" }}>
                {data.description || "No description provided."}
              </p>
            </div>
          </>
        )}
      </Modal.Body>

      <Modal.Footer className="bg-light d-flex justify-content-between">
        <div>
          {data?.status === "Pending" && isInsufficient && (
            <span className="text-danger small fw-semibold">
              <i className="bx bx-error-circle me-1 fs-5 align-middle"></i>
              Cannot confirm: Insufficient source funds.
            </span>
          )}
          {data?.status === "Pending" && hasNoCutoff && (
            <span className="text-danger small fw-semibold ms-2">
              <i className="bx bx-error-circle me-1 fs-5 align-middle"></i>
              Cannot confirm: No active cutoff covers this process date.
            </span>
          )}
        </div>
        <div>
          <Button
            variant="secondary"
            onClick={onHide}
            className="me-2"
            disabled={isConfirming}
          >
            Close
          </Button>

          {data?.status === "Pending" && (
            <>
              {/* <Button
                variant="danger"
                className="me-2"
                onClick={handleReject}
                disabled={isConfirming}
              >
                <i className="bx bx-x-circle me-1" /> Reject
              </Button> */}

              <Button
                variant="success"
                onClick={handleConfirm}
                disabled={isConfirming || isInsufficient || hasNoCutoff}
              >
                {isConfirming ? (
                  <>
                    <Spinner size="sm" className="me-1" /> Confirming...
                  </>
                ) : (
                  <>
                    <i className="bx bx-check-circle me-1" /> Confirm
                  </>
                )}
              </Button>
            </>
          )}
        </div>
      </Modal.Footer>
    </Modal>
  );
};

export { CashFlowView };
